import { useMutation } from '@apollo/client/react';
import {
  AddUserToWaitListDocument,
  AddUserToWaitListInput,
} from '@website/types/graphqlOperations';
import { useState } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormGroup,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';

type Props = {
  groupID: string;
  userID: string;
};

/**
 * Join WaitList Button
 */
export default function JoinWaitListButton({ groupID, userID }: Props) {
  const [show, setShow] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<AddUserToWaitListInput>({
    mode: 'onBlur',
    defaultValues: {
      groupID,
      userID,
      spacesRequested: 1,
    },
  });

  const [addUserToWaitList, { loading }] = useMutation(
    AddUserToWaitListDocument,
    {
      update(cache, { data }) {
        const userCacheID = cache.identify({
          __typename: 'User',
          id: data.addUserToWaitList.user.id,
        });

        const waitListCacheID = cache.identify({
          __typename: 'WaitList',
          id: data.addUserToWaitList.id,
        });

        // add the waitlist to the identified group waitlist cache
        cache.modify({
          id: userCacheID,
          fields: {
            waitList(cachedWaitListItems) {
              return {
                items: [
                  ...cachedWaitListItems.items,
                  { __ref: waitListCacheID },
                ],
              };
            },
          },
        });
      },
    },
  );

  const onSubmit = async (fd: AddUserToWaitListInput) => {
    try {
      await addUserToWaitList({
        variables: {
          input: fd,
        },
      });
    } catch (e) {
      if (e.networkError) {
        alert(
          'Internet connection lost - please retry when you are connected.',
        );
      } else {
        alert(e);
      }
    }
  };

  return (
    <>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>How many spaces do you need?</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <FormGroup controlId="formBasicEmail">
              <FormControl
                as="input"
                type="number"
                placeholder="Number of spaces needed"
                name="note"
                required
                {...register('spacesRequested', { required: true })}
              />
            </FormGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button disabled={!isValid} type="submit" variant="danger">
              {loading ? <>Joining Waitlist</> : 'Join Waitlist'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Button
        role="button"
        size="sm"
        variant="light"
        onClick={() => setShow(true)}
      >
        {loading && (
          <>
            <Spinner
              data-testid="joining-spinner"
              animation="border"
              size="sm"
              className="me-1"
            />
          </>
        )}
        Wait
        <br className="d-md-none" />
        list
      </Button>
    </>
  );
}
